import { create } from "zustand";
import { devtools, persist } from "zustand/middleware";
import { immer } from "zustand/middleware/immer";

export interface OfferChecklistState {
    data: {
        PROTECTION: boolean;
        NON_PARTNER_DEVICES: boolean;
        TECH_SUPPORT: boolean;
        FUTURE_DEVICES: boolean;
        IN_HOME_INSTALLATION: boolean;
        IN_STORE_SERVICES: boolean;
        ASK_SALE: boolean;
        BILLING: boolean;
        TRANSITION: boolean;
        COVERAGE: boolean;
        RAPPORT: boolean;
        DEVICE_CUE: boolean;
        ENGAGEMENT_QUESTION: boolean;
        DISCOVERY_QUESTION: boolean;
    };
}

export type OfferChecklistKey = keyof OfferChecklistState["data"];

export interface OfferChecklistStore {
    offerChecklistsBySession: Record<string, OfferChecklistState>;
    getData: (sessionId: string) => OfferChecklistState["data"];
    setData: (sessionId: string, newData: Partial<OfferChecklistState["data"]>) => void;
    resetData: (sessionId: string) => void;
}

const defaultOfferChecklistState = {
    PROTECTION: false,
    NON_PARTNER_DEVICES: false,
    TECH_SUPPORT: false,
    FUTURE_DEVICES: false,
    IN_HOME_INSTALLATION: false,
    IN_STORE_SERVICES: false,
    ASK_SALE: false,
    BILLING: false,
    TRANSITION: false,
    COVERAGE: false,
    RAPPORT: false,
    DEVICE_CUE: false,
    ENGAGEMENT_QUESTION: false,
    DISCOVERY_QUESTION: false,
};

export const useOfferChecklistStore = create<OfferChecklistStore>()(
    persist(
        immer(
            devtools(
                (set, get) => ({
                    offerChecklistsBySession: {},
                    getData: (sessionId: string) =>
                        get().offerChecklistsBySession[sessionId]?.data ?? defaultOfferChecklistState,
                    setData: (sessionId: string, newData: Partial<OfferChecklistState["data"]>) => {
                        set(
                            (state: OfferChecklistStore) => {
                                const existingData =
                                    state.offerChecklistsBySession[sessionId]?.data ?? defaultOfferChecklistState;
                                state.offerChecklistsBySession[sessionId] = {
                                    data: { ...existingData, ...newData },
                                };
                            },
                            false,
                            "setData",
                        );
                    },
                    resetData: (sessionId: string) => {
                        set((state: OfferChecklistStore) => {
                            delete state.offerChecklistsBySession[sessionId];
                        });
                    },
                }),
                {
                    enabled: import.meta.env.VITE_ENV_TYPE !== "production",
                    store: "offer-checklist-store",
                    name: "workspace/sales-tools",
                },
            ),
        ),
        { name: "offer-checklist-store", skipHydration: true },
    ),
);
